import type { GameCoord } from "./coords.ts";
import { bearingDegrees, bearingMils, distanceMeters } from "./coords.ts";

/**
 * Range rings and firing geometry.
 *
 * The same maths serves opposite purposes depending on who is looking:
 * an artillery crew wants to know whether a target is inside its envelope,
 * while a pilot wants to know whether a route passes inside someone else's.
 * So a ring is modelled once and tagged as `friendly` or `threat`.
 */

export type EmplacementKind = "indirect" | "antiAir" | "direct";

export interface WeaponProfile {
  readonly id: string;
  readonly label: string;
  readonly kind: EmplacementKind;
  /** Minimum engagement range in meters. Rounds cannot fall closer than this. */
  readonly minRangeMeters: number;
  /** Maximum engagement range in meters. */
  readonly maxRangeMeters: number;
  /** False until the figures have been confirmed against the game. */
  readonly verified: boolean;
  /** Where the numbers came from, so a wrong value can be traced. */
  readonly source: string;
}

/**
 * Confirmed indirect-fire weapons.
 *
 * Ranges come from the open-source WARDOGS Calculator's `data/weapons.json`
 * (Apollyon, MIT licence). Anti-air figures are not published anywhere yet and
 * are left to the user to enter, rather than invented here.
 */
export const WEAPON_PROFILES: readonly WeaponProfile[] = [
  {
    id: "l81-mortar",
    label: "L81 Mortar",
    kind: "indirect",
    minRangeMeters: 132,
    maxRangeMeters: 684,
    verified: true,
    source: "wardogs-calculator data/weapons.json (MIT)",
  },
  {
    id: "sph-2",
    label: "SPH-2",
    kind: "indirect",
    minRangeMeters: 780,
    maxRangeMeters: 2629,
    verified: true,
    source: "wardogs-calculator data/weapons.json (MIT)",
  },
];

const WEAPONS_BY_ID = new Map<string, WeaponProfile>(
  WEAPON_PROFILES.map((weapon) => [weapon.id, weapon]),
);

export function getWeaponProfile(id: string): WeaponProfile | undefined {
  return WEAPONS_BY_ID.get(id);
}

export type RangeStatus = "inRange" | "tooClose" | "tooFar";

export interface FiringSolution {
  readonly distanceMeters: number;
  readonly bearingDegrees: number;
  readonly bearingMils: number;
  readonly status: RangeStatus;
  /** How far outside the envelope the target sits, in meters. Zero when in range. */
  readonly outByMeters: number;
}

/**
 * Distance, bearing and range check from a gun position to a target.
 *
 * Deliberately does NOT produce an elevation or MIL firing solution: that
 * needs ballistic tables this project does not own, and a guessed elevation
 * is worse than none. Use the linked artillery calculator for the actual
 * elevation, and this for placement and range checking.
 */
export function solveFiring(
  gun: GameCoord,
  target: GameCoord,
  weapon: WeaponProfile,
): FiringSolution {
  const distance = distanceMeters(gun, target);

  let status: RangeStatus = "inRange";
  let outBy = 0;
  if (distance < weapon.minRangeMeters) {
    status = "tooClose";
    outBy = weapon.minRangeMeters - distance;
  } else if (distance > weapon.maxRangeMeters) {
    status = "tooFar";
    outBy = distance - weapon.maxRangeMeters;
  }

  return {
    distanceMeters: distance,
    bearingDegrees: bearingDegrees(gun, target),
    bearingMils: bearingMils(gun, target),
    status,
    outByMeters: outBy,
  };
}

export interface RangeRing {
  readonly center: GameCoord;
  readonly radiusMeters: number;
  readonly label: string;
  readonly kind: "min" | "max" | "custom";
}

/** The min/max rings to draw for a weapon sitting at a position. */
export function weaponRings(center: GameCoord, weapon: WeaponProfile): RangeRing[] {
  const rings: RangeRing[] = [];
  if (weapon.minRangeMeters > 0) {
    rings.push({
      center,
      radiusMeters: weapon.minRangeMeters,
      label: `${weapon.label} min`,
      kind: "min",
    });
  }
  rings.push({
    center,
    radiusMeters: weapon.maxRangeMeters,
    label: `${weapon.label} max`,
    kind: "max",
  });
  return rings;
}

/** Whether a point falls inside a ring's radius. */
export function isInsideRing(point: GameCoord, ring: RangeRing): boolean {
  return distanceMeters(point, ring.center) <= ring.radiusMeters;
}

/**
 * Sample a straight route and report whether it clips a threat radius.
 *
 * This is the pilot's version of a range check: not "can I hit it" but
 * "does my ingress pass through it". Sampling is linear and cheap; the
 * resolution is chosen so a 16 km leg still gets sub-100 m granularity.
 */
export interface ThreatExposure {
  readonly exposed: boolean;
  /** Fraction of the route inside the radius, in [0, 1]. */
  readonly fraction: number;
  /** Closest approach to the threat centre along the route, in meters. */
  readonly closestApproachMeters: number;
}

export function routeExposure(
  waypoints: readonly GameCoord[],
  ring: RangeRing,
  samplesPerLeg = 64,
): ThreatExposure {
  if (waypoints.length < 2) {
    const only = waypoints[0];
    if (!only) {
      return { exposed: false, fraction: 0, closestApproachMeters: Number.POSITIVE_INFINITY };
    }
    const distance = distanceMeters(only, ring.center);
    return {
      exposed: distance <= ring.radiusMeters,
      fraction: distance <= ring.radiusMeters ? 1 : 0,
      closestApproachMeters: distance,
    };
  }

  let inside = 0;
  let total = 0;
  let closest = Number.POSITIVE_INFINITY;

  for (let leg = 1; leg < waypoints.length; leg++) {
    const from = waypoints[leg - 1];
    const to = waypoints[leg];
    if (!from || !to) continue;

    for (let step = 0; step <= samplesPerLeg; step++) {
      const t = step / samplesPerLeg;
      const point: GameCoord = {
        x: from.x + (to.x - from.x) * t,
        y: from.y + (to.y - from.y) * t,
      };
      const distance = distanceMeters(point, ring.center);
      if (distance < closest) closest = distance;
      if (distance <= ring.radiusMeters) inside++;
      total++;
    }
  }

  return {
    exposed: inside > 0,
    fraction: total === 0 ? 0 : inside / total,
    closestApproachMeters: closest,
  };
}
